
// =============================================
// SHARED CONSTANTS
// =============================================

export const VALUELESS_OPERATORS = new Set(['IS_NULL', 'IS_NOT_NULL', 'THIS_QUARTER']);

export const SEGMENT_TIMEZONES: readonly string[] = [
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Chicago',
  'America/Mexico_City',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Buenos_Aires',
  'America/Caracas',
  'America/La_Paz',
  'America/Asuncion',
  'America/Montevideo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Lisbon',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Hong_Kong',
  'Asia/Bangkok',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Asia/Seoul',
  'Africa/Cairo',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

// =============================================
// PAGINACION
// =============================================

export interface PageResponse<T> {
    content: T[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

// =============================================
// FOLDERS
// =============================================

export interface SegmentFolder {
    id: number;
    bussinesId: number;
    name: string;
    segmentCount: number;
    datetimeCreated: string;
    datetimeUpdated: string;
}

export interface SegmentFolderRequest {
    bussinesId: number;
    name: string;
}

// =============================================
// FILTER RULES
// =============================================

export interface FilterRuleRequest {
    segmentId?: number;       // required for createRule, omitted in SegmentRequest.rules
    groupIndex: number;
    groupOperator?: string;
    property: string;
    operator: string;
    valueExpression?: string;
    executionOrder: number;
}

export interface SegmentSeedResponse {
    segmentId: number;
    totalAdded: number;
    totalMembers: number;
}

export interface FilterRuleSummary {
    id: number;
    groupIndex: number;
    groupOperator: 'AND' | 'OR';
    property: string;
    operator: string;
    valueExpression: string;
    executionOrder: number;
}


// =============================================
// SEGMENTS
// =============================================

export interface Segment {
    id: number;
    bussinesId: number;
    name: string;
    description?: string;
    type: 'STATIC' | 'DYNAMIC';
    objectType: 'CONTACT' | 'COMPANY' | 'DEAL';
    isSuppressionList: boolean;
    isSystem: boolean;
    status: 'DRAFT' | 'CALCULATING' | 'READY' | 'ERROR' | 'SUPPRESSED' | 'ARCHIVED';
    totalContacts: number;
    folderId?: number;
    folderName?: string;

    userPropertyId?: number;
    userPropertyName?: string;

    datetimeLastEvaluated?: string;
    datetimeCreated: string;
    datetimeUpdated: string;

    rules?: FilterRuleSummary[];
}

export interface SegmentRequest {
    bussinesId: number;
    name: string;
    description?: string;
    type: 'STATIC' | 'DYNAMIC';
    objectType: 'CONTACT' | 'COMPANY' | 'DEAL';
    isSuppressionList?: boolean;
    folderId?: number | null;
    userPropertyId?: number | null;
    rules?: FilterRuleRequest[];
}

export interface SegmentSummary {
    id: number;
    name: string;
}

// =============================================
// MEMBERS
// =============================================

export interface ContactSummary {
    contactId: number;
    name: string;
    email: string;
}

export interface SegmentMemberResponse {
    id: number;
    isExcluded: boolean;
    userActionId: number;
    userActionName: string;
    datetimeCreated: string;
    datetimeUpdated: string;
    segment: SegmentSummary;
    contact: ContactSummary;
}

// =============================================
// SCHEDULES
// =============================================

export type SegmentScheduleFrequency = 'MINUTES' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';

export interface SegmentScheduleRequest {
    segmentId: number;
    frecuency: SegmentScheduleFrequency;
    runTime?: string | null;        // "HH:mm:ss" — requerido en HOURLY/DAILY/WEEKLY/MONTHLY, ignorado en MINUTES
    runDays?: string[] | number[] | null; // WEEKLY: string[] (MON..SUN). MONTHLY: number[] (días del mes)
    intervalValue?: number | null;  // MINUTES: 1..59, HOURLY: 1..23
    enabled?: boolean;              // default true
    timezone?: string;              // IANA, default "UTC"
}

export interface SegmentScheduleResponse {
    id: number;
    frecuency: SegmentScheduleFrequency;
    runTime: string | null;
    runDays: string[] | number[] | null;
    intervalValue: number | null;
    enabled: boolean;
    timezone: string;
    scheduledJobConfigId: number;
    cronExpression: string;
    datetimeCreated: string;
    datetimeUpdated: string;
    segment: SegmentSummary;
}

export interface SegmentScheduleEnabledRequest {
    enabled: boolean;
}

// =============================================
// SET OPERATIONS
// =============================================

export interface SegmentSetOperationRequest {
    sourceSegmentIds: number[];
    operationType: 'UNION' | 'INTERSECT' | 'DIFFERENCE';
}

export interface SegmentSetOperationResponse {
    id: number;
    segmentResultId: number;
    segmentSourceId: number;
    segmentSourceName: string;
    operationType: string;
    executionOrder: number;
    datetimeCreated: string;
}

// =============================================
// IMPORT & BULK ACTIONS
// =============================================

export interface SegmentImportResponse {
    totalRows: number;
    added: number;
    duplicates: number;
    notFound: string[];
    errors: number;
}

export interface SegmentBulkActionRequest {
    action: 'CHANGE_OWNER' | 'EXCLUDE' | 'DELETE';
    memberIds: number[];
    params?: Record<string, unknown>;
}

export interface SegmentBulkActionResponse {
    action: string;
    totalRequested: number;
    processed: number;
    failed: number;
}

// =============================================
// SEGMENT SEARCH FILTERS
// =============================================

export interface SegmentSearchFilters {
    type?: string;
    status?: string;
    folderId?: number;
    searchTerm?: string;
}

export interface SegmentStatsResponse {
    total: number;
    dynamic: number;
    staticCount: number;
    suppressed: number;
}

// =============================================
// SEGMENT PREVIEW
// =============================================

export interface SegmentPreviewRequest {
    rules: FilterRuleRequest[];
    objectType?: string;
    excludeSegmentId?: number;
}

export interface SegmentPreviewContactResponse {
    id: number;
    fullname: string;
    lastname?: string;
    mail: string;
    phone?: string;
    position?: string;
}
